import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getWorkspace } from "../lib/workspaceStore";
import { api } from "../lib/api";
import { PageSkeleton } from "../components/PageSkeleton";
import { SoundVideo } from "../components/SoundVideo";

function numberValue(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function money(value) {
  return `$${numberValue(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function pct(value) {
  return `${numberValue(value).toFixed(1)}%`;
}

function textFromGeneration(item) {
  try {
    return JSON.stringify({ type: item.type, input: item.input_json, output: item.output_json }).toLowerCase();
  } catch {
    return String(item.type || "").toLowerCase();
  }
}

function assetTitle(item) {
  const input = item.input_json || {};
  const request = input.request || input;
  const output = item.output_json || {};
  return (
    request.product_or_service
    || input.product_or_service
    || output.product_or_service
    || output.offer
    || output.headline
    || item.type
    || "Workspace creative"
  );
}

function assetHook(item) {
  const output = item.output_json || {};
  return (
    output.hook
    || output.headline
    || output.primary_text
    || output.cole_creative?.hook
    || output.cole_creative?.headline
    || output.results?.cole_creative?.hook
    || output.results?.cole_creative?.headline
    || "Saved inside OmniMark Library"
  );
}

function buildBudgetPlan(campaign, spend, clicks, conversions) {
  const input = campaign?.input || {};
  const objective = input.objective || input.campaign_goal || "sales";
  const videos = numberValue(input.video_tests || 3) || 3;
  const dailyBase = objective === "sales" ? 35 : objective === "leads" ? 25 : objective === "product_test" ? 20 : 18;
  const dailyMin = dailyBase + (videos - 2) * 5;
  const dailyMax = dailyMin * 2;
  const totalSpend = numberValue(spend || campaign?.metrics?.spend);
  const totalClicks = numberValue(clicks || campaign?.metrics?.clicks);
  const totalConversions = numberValue(conversions || campaign?.metrics?.conversions);
  const cpa = totalConversions > 0 ? totalSpend / totalConversions : 0;
  const ctrReady = totalClicks > 0;
  const stage = totalSpend < dailyMin ? "Learning" : totalConversions > 0 ? "Proof" : "Needs signal";
  return {
    objective,
    videos,
    dailyMin,
    dailyMax,
    testBudget: dailyMin * 3,
    stage,
    cpa,
    guidance: [
      `Start with ${money(dailyMin)}-${money(dailyMax)} per day until each video has enough impressions to judge.`,
      `Use ${videos} creative angles before scaling. Do not raise budget until one angle has clear CTR, CPA, or lead quality.`,
      ctrReady ? "Clicks are coming in. Watch landing page fit, comments, and conversion quality before increasing spend." : "No click signal yet. Test hooks, first three seconds, offer clarity, and audience match.",
      cpa ? `Current CPA reads around ${money(cpa)}. Scale only if this fits the client's margin or lead value.` : "CPA is not ready yet because conversions have not been entered.",
    ],
  };
}

function buildCheckoutOptions(budgetPlan) {
  const testAmount = Math.max(50, Math.round(budgetPlan.testBudget));
  const learningAmount = Math.max(100, Math.round(budgetPlan.dailyMin * 7));
  const scaleAmount = Math.max(250, Math.round(budgetPlan.dailyMax * 7));
  return [
    {
      id: "three_day_test",
      name: "3-day proof test",
      amount: testAmount,
      note: "Best first checkout when the user does not know what to spend yet.",
      rhythm: `${money(budgetPlan.dailyMin)}-${money(budgetPlan.dailyMax)}/day`,
    },
    {
      id: "seven_day_learning",
      name: "7-day learning run",
      amount: learningAmount,
      note: "Gives OmniMark enough signal to compare videos, audiences, and landing page fit.",
      rhythm: `${money(Math.round(learningAmount / 7))}/day`,
    },
    {
      id: "scale_watch",
      name: "Scale watch",
      amount: scaleAmount,
      note: "Use after proof. OmniMark watches CPA, fatigue, comments, and budget jumps.",
      rhythm: `${money(Math.round(scaleAmount / 7))}/day`,
    },
  ];
}

function buildLiveTasks(campaign, budgetPlan) {
  const input = campaign?.input || {};
  const team = input.ai_campaign_team || [];
  const base = team.length ? team.slice(0, 5) : [
    { avatar: "Luna Growth", role: "Budget captain", channel: campaign?.platform || "Meta" },
    { avatar: "Cole Creative", role: "Creative testing lead", channel: "Video ads" },
    { avatar: "Sage Conversion", role: "Landing page coach", channel: "Customer site" },
  ];
  return base.map((member, index) => ({
    name: member.avatar || member.name || "OmniMark AI",
    role: member.role || "Campaign analyst",
    channel: member.channel || "Campaign",
    status: index === 0 ? budgetPlan.stage : index % 2 === 0 ? "Watching" : "Ready",
    action: member.action || [
      "Check daily spend, CPA, and whether budget should pause or scale.",
      "Review video hooks and refresh weak creative before spend is wasted.",
      "Compare ad promise to landing page, form, chat, and customer questions.",
      "Look for winning audience signals and duplicate what is working.",
      "Prepare after-campaign notes for the client in plain language.",
    ][index % 5],
  }));
}

export default function CampaignDetailPage() {
  const { campaignId } = useParams();
  const ws = getWorkspace();
  const qc = useQueryClient();

  const [campaign, setCampaign] = useState(null);
  const [debrief, setDebrief] = useState(null);
  const [libraryItems, setLibraryItems] = useState([]);
  const [videoJobs, setVideoJobs] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const [spend, setSpend] = useState("");
  const [impressions, setImpressions] = useState("");
  const [clicks, setClicks] = useState("");
  const [conversions, setConversions] = useState("");
  const [selectedCheckout, setSelectedCheckout] = useState("three_day_test");

  const load = useCallback(async () => {
    if (!ws?.id) return;
    setErr(null);
    setLoading(true);
    try {
      const cRes = await api.get(`/api/workspaces/${ws.id}/campaigns/${campaignId}`);
      const loadedCampaign = cRes.data;
      setCampaign(loadedCampaign);
      setSpend(loadedCampaign.metrics?.spend ?? "");
      setImpressions(loadedCampaign.metrics?.impressions ?? "");
      setClicks(loadedCampaign.metrics?.clicks ?? "");
      setConversions(loadedCampaign.metrics?.conversions ?? "");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load campaign command room.");
    }

    try {
      const libraryRes = await api.get(`/api/workspaces/${ws.id}/generations`);
      setLibraryItems(libraryRes.data.items || []);
    } catch {
      setLibraryItems([]);
    }

    try {
      const jobsRes = await api.get(`/api/workspaces/${ws.id}/avatar/jobs`);
      setVideoJobs(jobsRes.data.items || []);
    } catch {
      setVideoJobs([]);
    }

    try {
      const dRes = await api.get(`/api/workspaces/${ws.id}/campaigns/${campaignId}/debriefs/latest`);
      setDebrief(dRes.data);
    } catch {
      setDebrief(null);
    } finally {
      setLoading(false);
    }
  }, [ws?.id, campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const budgetPlan = useMemo(() => buildBudgetPlan(campaign, spend, clicks, conversions), [campaign, clicks, conversions, spend]);
  const liveTasks = useMemo(() => buildLiveTasks(campaign, budgetPlan), [budgetPlan, campaign]);
  const input = campaign?.input || {};
  const briefings = input.ai_coach_briefings || {};
  const metrics = {
    spend: numberValue(spend || campaign?.metrics?.spend),
    impressions: numberValue(impressions || campaign?.metrics?.impressions),
    clicks: numberValue(clicks || campaign?.metrics?.clicks),
    conversions: numberValue(conversions || campaign?.metrics?.conversions),
  };
  const providerMetricsReady = input.metric_source === "provider" && input.metric_provider;
  const ctr = metrics.impressions ? (metrics.clicks / metrics.impressions) * 100 : 0;
  const cvr = metrics.clicks ? (metrics.conversions / metrics.clicks) * 100 : 0;
  const checkoutOptions = useMemo(() => buildCheckoutOptions(budgetPlan), [budgetPlan]);
  const checkoutPlan = checkoutOptions.find((option) => option.id === selectedCheckout) || checkoutOptions[0];
  const managedFee = Math.round(checkoutPlan.amount * 0.02);
  const checkoutUrl = `/app/upgrade?source=campaign_wallet&campaign=${encodeURIComponent(campaignId)}&ad_budget=${checkoutPlan.amount}&plan=${encodeURIComponent(checkoutPlan.id)}`;

  const creativeAssets = useMemo(() => {
    const campaignText = `${campaign?.name || ""} ${input.campaign_lookup_context?.product_or_service || ""}`.toLowerCase();
    const matched = libraryItems.filter((item) => {
      const text = textFromGeneration(item);
      return text.includes("ad") || text.includes("studio") || campaignText.split(" ").some((word) => word.length > 5 && text.includes(word));
    });
    return matched.slice(0, 6);
  }, [campaign?.name, input.campaign_lookup_context?.product_or_service, libraryItems]);
  const campaignVideoJobs = useMemo(() => {
    return [...videoJobs].sort((a, b) => {
      const aReady = a.output?.video_url ? 1 : 0;
      const bReady = b.output?.video_url ? 1 : 0;
      return bReady - aReady || String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });
  }, [videoJobs]);

  async function endAndGenerateDebrief() {
    setErr(null);
    setBusy(true);
    try {
      await api.post(`/api/workspaces/${ws.id}/campaigns/${campaignId}/end`, {
        metrics: {
          spend: spend ? Number(spend) : undefined,
          impressions: impressions ? Number(impressions) : undefined,
          clicks: clicks ? Number(clicks) : undefined,
          conversions: conversions ? Number(conversions) : undefined,
        },
      });
      qc.invalidateQueries({ queryKey: ["campaigns", ws.id] });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to end campaign / generate debrief.");
    } finally {
      setBusy(false);
    }
  }

  async function generateV2Pack() {
    if (!debrief?.id) return;
    setBusy(true);
    setErr(null);
    try {
      const idemKey = `v2_${campaignId}_${debrief.id}_${Date.now()}`;
      const res = await api.post(
        `/api/workspaces/${ws.id}/campaigns/${campaignId}/debriefs/${debrief.id}/generate-pack`,
        { auto_run: true, idempotency_key: idemKey }
      );
      qc.invalidateQueries({ queryKey: ["packs", ws.id] });
      qc.invalidateQueries({ queryKey: ["library", ws.id] });
      const genId = res.data.generation_id;
      window.location.href = genId ? `/app/library/${genId}` : "/app/studio/packs";
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to generate V2 pack.");
    } finally {
      setBusy(false);
    }
  }

  async function openAdSpendCheckout() {
    setErr(null);
    setCheckoutBusy(true);
    try {
      const res = await api.post("/api/billing/ad-spend-checkout-session", {
        amount: checkoutPlan.amount,
        campaign_id: campaignId,
        workspace_id: ws.id,
        plan_id: checkoutPlan.id,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not open ad-spend checkout. Use the upgrade checkout while Stripe wallet setup is finished.");
      window.history.replaceState(null, "", checkoutUrl);
    } finally {
      setCheckoutBusy(false);
    }
  }

  async function syncVideoJob(jobId) {
    if (!ws?.id || !jobId) return;
    setErr(null);
    try {
      await api.post(`/api/workspaces/${ws.id}/avatar/jobs/${jobId}/sync`);
      const jobsRes = await api.get(`/api/workspaces/${ws.id}/avatar/jobs`);
      setVideoJobs(jobsRes.data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not refresh video status yet.");
    }
  }

  if (!ws) return <div style={{ padding: 16, color: "#666" }}>Please select a workspace first.</div>;
  if (loading) return <PageSkeleton />;
  if (!campaign) return <div style={{ padding: 16 }}>Campaign not found.</div>;

  const report = debrief?.report;

  return (
    <div style={page} data-testid="campaign-detail-page">
      <section style={hero}>
        <div>
          <div style={eyebrow}>Campaign command room</div>
          <h1 style={title}>{campaign.name}</h1>
          <div style={heroMeta}>
            <span>{campaign.platform.toUpperCase()}</span>
            <span>{campaign.status}</span>
            <span>{budgetPlan.stage}</span>
          </div>
        </div>
        <div style={heroActions}>
          <Link to="/app/studio/avatar" style={lightButton}>Create video</Link>
          <Link to="/app/library" style={lightButton}>Open Library</Link>
        </div>
      </section>

      {err && <div style={errorBox}>{err}</div>}

      <section style={gridTwo}>
        <div style={panelDark}>
          <div style={panelTop}>
            <div>
              <div style={eyebrowDark}>Spend guide</div>
              <div style={sectionTitle}>What should I spend?</div>
            </div>
            <span style={statusPill}>{budgetPlan.stage}</span>
          </div>
          <div style={budgetCards}>
            <Metric label="Suggested daily test" value={`${money(budgetPlan.dailyMin)}-${money(budgetPlan.dailyMax)}`} />
            <Metric label="3-day proof budget" value={money(budgetPlan.testBudget)} />
            <Metric label="Creative tests" value={`${budgetPlan.videos} videos`} />
            <Metric label="Current CPA" value={budgetPlan.cpa ? money(budgetPlan.cpa) : "Waiting"} />
          </div>
          <div style={taskList}>
            {budgetPlan.guidance.map((item) => <div key={item} style={darkTask}>{item}</div>)}
          </div>
        </div>

        <div style={panel}>
          <div style={sectionTitle}>Live AI Team Tools</div>
          <p style={muted}>The team should help before, during, and after launch. These jobs stay inside the workspace.</p>
          <div style={taskList}>
            {liveTasks.map((task) => (
              <div key={`${task.name}-${task.role}`} style={teamTask}>
                <div style={avatarDot}>{task.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</div>
                <div>
                  <div style={taskTop}>
                    <strong>{task.name}</strong>
                    <span style={miniPill}>{task.status}</span>
                  </div>
                  <div style={mutedSmall}>{task.role} • {task.channel}</div>
                  <div style={taskAction}>{task.action}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={checkoutPanel}>
        <div style={panelTop}>
          <div>
            <div style={eyebrowDark}>Payment + ad spend</div>
            <div style={sectionTitle}>Ad Spend Checkout</div>
            <p style={mutedOnDark}>
              Fund the campaign like a launch wallet: ad-platform spend stays separate from OmniMark subscription and AI usage. This keeps the checkout clear before money starts moving.
            </p>
          </div>
          <span style={statusPillDark}>Recommended: {money(checkoutPlan.amount)}</span>
        </div>

        <div style={checkoutGrid}>
          {checkoutOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setSelectedCheckout(option.id)}
              style={option.id === checkoutPlan.id ? checkoutOptionActive : checkoutOption}
            >
              <span>{option.name}</span>
              <strong>{money(option.amount)}</strong>
              <em>{option.rhythm}</em>
              <small>{option.note}</small>
            </button>
          ))}
        </div>

        <div style={ledgerGrid}>
          <Metric label="Ad platform budget" value={money(checkoutPlan.amount)} />
          <Metric label="OmniMark AI usage" value={input.ai_team_preset || "Plan + credits"} />
          <Metric label="Optional managed fee" value={`2% preview: ${money(managedFee)}`} />
          <Metric label="Payment status" value="Ready to fund" />
        </div>

        <div style={checkoutActions}>
          <button type="button" onClick={openAdSpendCheckout} disabled={checkoutBusy} style={checkoutButton}>
            {checkoutBusy ? "Opening checkout..." : "Open payment checkout"}
          </button>
          <Link to="/app/connections" style={checkoutSecondary}>Connect ad account</Link>
          <div style={checkoutNote}>
            If users connect Meta, TikTok, Google, or Stripe revenue, OmniMark can show spend, ROAS, usage, and remaining budget in this room.
          </div>
        </div>
      </section>

      <section style={metricsPanel}>
        <div style={panelTop}>
          <div>
            <div style={sectionTitle}>Campaign Metrics</div>
            <div style={mutedSmall}>
              {providerMetricsReady
                ? `Live provider sync: ${input.metric_provider}`
                : "Manual metrics until a provider token is exchanged, verified, and linked to this campaign."}
            </div>
          </div>
          <Link to="/app/connections" style={smallButton}>{providerMetricsReady ? "Connection details" : "Connect live metrics"}</Link>
        </div>
        <div style={metricsGrid}>
          <input value={spend} onChange={(e) => setSpend(e.target.value)} placeholder="Spend ($)" style={inputStyle} />
          <input value={impressions} onChange={(e) => setImpressions(e.target.value)} placeholder="Impressions" style={inputStyle} />
          <input value={clicks} onChange={(e) => setClicks(e.target.value)} placeholder="Clicks" style={inputStyle} />
          <input value={conversions} onChange={(e) => setConversions(e.target.value)} placeholder="Conversions" style={inputStyle} />
        </div>
        <div style={statRow}>
          <Metric label="CTR" value={pct(ctr)} />
          <Metric label="Conversion rate" value={pct(cvr)} />
          <Metric label="Cost per click" value={metrics.clicks ? money(metrics.spend / metrics.clicks) : "Waiting"} />
          <Metric label="Cost per conversion" value={metrics.conversions ? money(metrics.spend / metrics.conversions) : "Waiting"} />
        </div>
      </section>

      <section style={gridTwo}>
        <div style={panel}>
          <div style={panelTop}>
            <div>
              <div style={sectionTitle}>Videos + Studio Jobs</div>
              <p style={muted}>Videos are stored in OmniMark workspace jobs. Customers can view and reuse them here instead of saving them to personal devices.</p>
            </div>
            <Link to="/app/studio/avatar" style={smallButton}>Create video</Link>
          </div>
          {campaignVideoJobs.length === 0 ? (
            <div style={emptyBox}>
              No video jobs yet. Create avatar videos from Avatar Studio, then they will appear in this campaign workspace vault.
            </div>
          ) : (
            <div style={assetGrid}>
              {campaignVideoJobs.map((job) => (
                <div key={job.id} style={assetCard}>
                  <strong>{job.presenter_key || "OmniMark avatar video"}</strong>
                  <span style={mutedSmall}>{job.status}</span>
                  {(() => {
                    const playbackUrl = job.playback_url || job.output?.omnimark_video_url || job.output?.video_url;
                    const jobError = job.output?.error || job.output?.message;
                    return playbackUrl ? (
                    <>
                      <SoundVideo src={playbackUrl} style={videoStyle} />
                      {job.output?.omnimark_video_url && <span style={storagePill}>OmniMark optimized</span>}
                      <div style={videoActions}>
                        <a href={playbackUrl} target="_blank" rel="noreferrer" style={inlineLink}>Open full video</a>
                        <button type="button" onClick={() => syncVideoJob(job.id)} style={refreshButton}>Refresh</button>
                      </div>
                    </>
                    ) : (
                    <div style={jobError ? videoFailed : videoEmpty}>
                      <span>{jobError || "Processing inside OmniMark"}</span>
                      <button type="button" onClick={() => syncVideoJob(job.id)} style={refreshButtonDark}>Refresh status</button>
                    </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={panel}>
          <div style={panelTop}>
            <div>
              <div style={sectionTitle}>Campaign Creative Vault</div>
              <p style={muted}>Ad packs, scripts, hooks, and generated creative stay saved in the workspace Library.</p>
            </div>
            <Link to="/app/ads" style={smallButton}>Generate ad pack</Link>
          </div>
          {creativeAssets.length === 0 ? (
            <div style={emptyBox}>No saved creative matched yet. Generate an ad pack or studio asset and OmniMark will keep it in Library.</div>
          ) : (
            <div style={assetGrid}>
              {creativeAssets.map((item) => (
                <div key={item.id} style={assetCard}>
                  <span style={miniPill}>{item.type}</span>
                  <strong>{assetTitle(item)}</strong>
                  <p>{assetHook(item)}</p>
                  <Link to="/app/library" style={inlineLink}>View in Library</Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section style={gridThree}>
        <CoachCard title="Before launch" text={briefings.before || "Confirm offer, audience, landing page, tracking, budget, and video tests before spend starts."} />
        <CoachCard title="During campaign" text={briefings.during || "Watch spend, CTR, CPA, comments, creative fatigue, and landing-page fit every day."} />
        <CoachCard title="After campaign" text={briefings.after || "Explain what won, what wasted spend, and what OmniMark should build next."} />
      </section>

      <section style={panel}>
        <div style={panelTop}>
          <div>
            <div style={sectionTitle}>AI Coach Debrief</div>
            <p style={muted}>End the campaign when ready. OmniMark will turn the results into a next-step pack saved back into the workspace.</p>
          </div>
          <button onClick={endAndGenerateDebrief} disabled={busy} style={btn}>
            {busy ? "Generating..." : "End + Debrief"}
          </button>
        </div>

        {!report ? (
          <div style={emptyBox}>No debrief yet. Enter current metrics above, then end the campaign to generate one.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            <ResultCard title="What went right" items={report.what_went_right} />
            <ResultCard title="What went wrong" items={report.what_went_wrong} />
            <ResultCard title="What to improve next" items={report.what_to_improve} />
            <button style={btnOutline} onClick={generateV2Pack} disabled={busy}>
              {busy ? "Generating V2 Pack..." : "Generate V2 Pack from Debrief"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div style={metricCard}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function CoachCard({ title, text }) {
  return (
    <div style={coachCard}>
      <strong>{title}</strong>
      <p>{text}</p>
    </div>
  );
}

function ResultCard({ title, items }) {
  return (
    <div style={resultCard}>
      <div style={{ fontWeight: 900, marginBottom: 8 }}>{title}</div>
      {!items || items.length === 0 ? (
        <div style={mutedSmall}>No items yet.</div>
      ) : (
        <ul>
          {items.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      )}
    </div>
  );
}

const page = { padding: 16, maxWidth: 1180, margin: "0 auto", display: "grid", gap: 14 };
const hero = { borderRadius: 8, padding: 22, background: "linear-gradient(135deg,#07121f,#12392f 58%,#3b2f75)", color: "#fff", display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-end" };
const eyebrow = { color: "#9ee7d4", fontWeight: 900, fontSize: 12, textTransform: "uppercase", letterSpacing: 0 };
const eyebrowDark = { color: "#7dd3fc", fontWeight: 900, fontSize: 12, textTransform: "uppercase", letterSpacing: 0 };
const title = { margin: "6px 0", fontSize: 30, lineHeight: 1.12 };
const heroMeta = { display: "flex", gap: 8, flexWrap: "wrap", color: "rgba(255,255,255,.82)", fontSize: 13 };
const heroActions = { display: "flex", gap: 8, flexWrap: "wrap" };
const lightButton = { borderRadius: 8, background: "#fff", color: "#101828", padding: "10px 12px", fontWeight: 900, textDecoration: "none" };
const gridTwo = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 14 };
const gridThree = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 };
const panel = { borderRadius: 8, border: "1px solid rgba(0,0,0,.10)", background: "#fff", padding: 14, minWidth: 0 };
const panelDark = { ...panel, background: "#0f172a", color: "#fff", borderColor: "rgba(255,255,255,.12)" };
const checkoutPanel = { ...panelDark, background: "linear-gradient(135deg,#06131f,#102a43 52%,#143a32)", display: "grid", gap: 12 };
const panelTop = { display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, flexWrap: "wrap" };
const sectionTitle = { fontSize: 18, fontWeight: 900 };
const muted = { color: "#667085", margin: "6px 0 12px", fontSize: 13, lineHeight: 1.45 };
const mutedOnDark = { color: "rgba(255,255,255,.75)", margin: "6px 0 0", fontSize: 13, lineHeight: 1.45, maxWidth: 760 };
const mutedSmall = { color: "#667085", fontSize: 13, lineHeight: 1.4 };
const budgetCards = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8, marginTop: 12 };
const metricCard = { borderRadius: 8, border: "1px solid rgba(0,0,0,.10)", background: "rgba(255,255,255,.08)", padding: 10, display: "grid", gap: 4 };
const taskList = { display: "grid", gap: 8, marginTop: 10 };
const darkTask = { borderRadius: 8, background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.10)", padding: 10, color: "#e5e7eb", fontSize: 13, lineHeight: 1.45 };
const teamTask = { display: "grid", gridTemplateColumns: "42px 1fr", gap: 10, alignItems: "start", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, background: "#fbfcff" };
const avatarDot = { width: 42, height: 42, borderRadius: 999, display: "grid", placeItems: "center", background: "#111827", color: "#fff", fontWeight: 900 };
const taskTop = { display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" };
const taskAction = { marginTop: 6, color: "#344054", fontSize: 13, lineHeight: 1.4 };
const miniPill = { borderRadius: 999, background: "#ecfeff", color: "#155e75", padding: "3px 8px", fontSize: 12, fontWeight: 900, whiteSpace: "nowrap" };
const storagePill = { width: "fit-content", borderRadius: 999, background: "#dcfce7", color: "#166534", padding: "3px 8px", fontSize: 11, fontWeight: 900 };
const statusPill = { ...miniPill, background: "#dcfce7", color: "#166534" };
const statusPillDark = { ...miniPill, background: "rgba(255,255,255,.12)", color: "#fff", border: "1px solid rgba(255,255,255,.20)" };
const metricsPanel = { ...panel, display: "grid", gap: 10 };
const metricsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 };
const statRow = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8 };
const checkoutGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 10 };
const checkoutOption = { borderRadius: 8, border: "1px solid rgba(255,255,255,.16)", background: "rgba(255,255,255,.07)", color: "#fff", padding: 12, display: "grid", gap: 6, textAlign: "left", cursor: "pointer", fontFamily: "inherit" };
const checkoutOptionActive = { ...checkoutOption, borderColor: "#86efac", background: "rgba(34,197,94,.18)", boxShadow: "0 0 0 1px rgba(134,239,172,.25) inset" };
const ledgerGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8 };
const checkoutActions = { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" };
const checkoutButton = { borderRadius: 8, border: "none", background: "#22c55e", color: "#052e16", padding: "10px 12px", fontWeight: 900, textDecoration: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 14 };
const checkoutSecondary = { borderRadius: 8, background: "rgba(255,255,255,.10)", border: "1px solid rgba(255,255,255,.18)", color: "#fff", padding: "10px 12px", fontWeight: 900, textDecoration: "none" };
const checkoutNote = { color: "rgba(255,255,255,.72)", fontSize: 12, lineHeight: 1.35, maxWidth: 520 };
const assetGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 10 };
const assetCard = { border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, background: "#fbfcff", display: "grid", gap: 7, minWidth: 0 };
const emptyBox = { border: "1px dashed #cbd5e1", borderRadius: 8, padding: 14, background: "#f8fafc", color: "#475467", fontSize: 13, lineHeight: 1.45 };
const videoStyle = { width: "100%", borderRadius: 8, background: "#000", aspectRatio: "16 / 9" };
const videoActions = { display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" };
const videoEmpty = { borderRadius: 8, background: "#111827", color: "#fff", minHeight: 112, display: "grid", placeItems: "center", gap: 8, fontSize: 13, textAlign: "center", padding: 10 };
const videoFailed = { ...videoEmpty, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", lineHeight: 1.35 };
const refreshButton = { borderRadius: 8, border: "1px solid rgba(0,0,0,.14)", background: "#fff", color: "#111827", padding: "7px 9px", fontSize: 12, fontWeight: 900, cursor: "pointer" };
const refreshButtonDark = { ...refreshButton, borderColor: "rgba(255,255,255,.24)", background: "rgba(255,255,255,.12)", color: "#fff" };
const coachCard = { border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, background: "#fff", color: "#344054", lineHeight: 1.45 };
const resultCard = { border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, background: "#fbfcff" };
const inlineLink = { color: "#111827", fontWeight: 900, textDecoration: "none" };
const smallButton = { borderRadius: 8, border: "1px solid rgba(0,0,0,.12)", padding: "8px 10px", color: "#111827", textDecoration: "none", fontWeight: 900, background: "#fff" };
const errorBox = { borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", padding: 12 };
const inputStyle = { width: "100%", borderRadius: 12, border: "1px solid rgba(0,0,0,0.12)", padding: "10px 12px", outline: "none", background: "#fff", fontFamily: "inherit", fontSize: 14 };
const btn = { borderRadius: 12, border: "none", padding: "10px 12px", fontWeight: 900, background: "#111", color: "#fff", cursor: "pointer", fontSize: 14 };
const btnOutline = { borderRadius: 12, border: "1px solid rgba(0,0,0,0.12)", padding: "10px 12px", fontWeight: 900, background: "transparent", color: "#111", cursor: "pointer", fontSize: 14 };
